import { format as dateFnsFormat, parse as dateFnsParse } from 'date-fns'
import enUS from 'date-fns/locale/en-US'
import es from 'date-fns/locale/es'
import fr from 'date-fns/locale/fr'
import it from 'date-fns/locale/it'
import pl from 'date-fns/locale/pl'
import pt from 'date-fns/locale/pt-BR'
import React, { forwardRef, useEffect, useRef } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import type { ReactDatePickerProps } from 'react-datepicker'
import { useTranslation } from 'react-i18next'

import { formatDate } from '@hozana/dates'
import { useScreen } from '@hozana/screen/useScreen'
import { QA } from '@hozana/tests/constants'

import { TInputProps } from 'elements/input/Input'
import { Div } from 'elements/layout/Div'
import { Text } from 'elements/text/Text'

import { ClearableInput, TClearableInputProps } from './ClearableInput'
import { StyledDateInput } from './StyledDateInput'

if (__CLIENT__) {
  registerLocale('fr', fr)
  registerLocale('it', it)
  registerLocale('en-US', enUS)
  registerLocale('es', es)
  registerLocale('pl', pl)
  registerLocale('pt', pt)
}

const locales: { [key: string]: Locale } = { en: enUS, es, fr, it, pl, pt }

export type TDateInputProps = {
  'data-testid'?: string
  dateFormat?: string
  displayDateFormat?: string
  onChange(date: Date, event: React.SyntheticEvent<any, Event>): void
  openOnStart?: boolean
  value?: string
} & Pick<
  ReactDatePickerProps,
  'minDate' | 'maxDate' | 'withPortal' | 'timeCaption' | 'showTimeSelect' | 'showTimeSelectOnly' | 'timeIntervals'
> &
  Partial<Omit<TClearableInputProps, 'onChange'>>

const formatValueForDisplay = (
  value: string | number | readonly string[],
  dateFormat: string,
  displayDateFormat: string,
  language: string,
): string | number | readonly string[] => {
  if (value === '') {
    return value
  }
  if (dateFormat === displayDateFormat) {
    return value
  }
  return dateFnsFormat(dateFnsParse(value as string, dateFormat, new Date()), displayDateFormat, {
    locale: locales[language],
  })
}

type TCustomDateInputProps = TInputProps & {
  dateFormat: string
  displayDateFormat: string
  language: string
  onClear: TClearableInputProps['onClear']
}
const CustomDateInput = forwardRef<HTMLInputElement, TCustomDateInputProps>(
  ({ dateFormat, displayDateFormat, language, onClear, value, ...otherProps }, ref) => (
    <ClearableInput
      {...otherProps}
      onClear={onClear}
      readOnly
      ref={ref}
      value={formatValueForDisplay(value, dateFormat, displayDateFormat, language)}
    />
  ),
)
CustomDateInput.displayName = 'CustomDateInput'

/**
 * ### DateInput ###
 *
 * Give a date value to the DD/MM/YYYY format
 *
 * Cannot be uncontrolled for now.
 */
export const DateInput = React.forwardRef<HTMLInputElement, TDateInputProps>(
  (
    {
      dateFormat = 'dd/MM/yyyy',
      disabled,
      displayDateFormat,
      onChange,
      minDate,
      maxDate,
      openOnStart = false,
      onClear: propOnClear,
      showTimeSelect,
      showTimeSelectOnly,
      timeCaption,
      timeIntervals,
      value,
      withPortal,
      ...otherProps
    },
    inputRef,
  ) => {
    if (displayDateFormat === undefined) {
      displayDateFormat = dateFormat
    }
    const screen = useScreen()
    const {
      i18n: { language },
    } = useTranslation()

    const datepickerRef = useRef(null)

    const clearValue = () => onChange(undefined, undefined)

    const onClear: TClearableInputProps['onClear'] = (e) => {
      clearValue()
      propOnClear?.(e)
    }

    useEffect(() => {
      const datepickerElement = datepickerRef.current
      if (openOnStart) {
        datepickerElement.setOpen(true)
      }
    }, [openOnStart])

    return (
      <StyledDateInput>
        <DatePicker
          data-testid={QA.COMMON.DATE_PICKER}
          customInput={
            <CustomDateInput
              dateFormat={dateFormat}
              displayDateFormat={displayDateFormat}
              language={language}
              onClear={onClear}
              ref={inputRef}
              {...otherProps}
            />
          }
          disabled={disabled}
          onChange={onChange}
          // Clear the input onMonthChange and onYearChange to show that the new date
          // is not taken into account as long as the user don't click on the date number
          timeCaption={timeCaption}
          showTimeSelect={showTimeSelect}
          showTimeSelectOnly={showTimeSelectOnly}
          timeIntervals={timeIntervals}
          onMonthChange={clearValue}
          onYearChange={clearValue}
          locale={language}
          minDate={minDate}
          maxDate={maxDate}
          dateFormat={dateFormat}
          withPortal={withPortal ?? screen.xs}
          peekNextMonth
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          ref={datepickerRef}
          // Prevent on screen keyboard to pop open on mobiles and half hiding the date input
          onFocus={(e) => e.target.blur()}
          {...(value && { value })}
        >
          {!showTimeSelectOnly && (
            <Div m="5px" align="center">
              <Text>trans:common:common.form.datepicker.choose-date</Text>
            </Div>
          )}
        </DatePicker>
      </StyledDateInput>
    )
  },
)

DateInput.displayName = 'DateInput'

export type TLegacyDateInputProps = Omit<TDateInputProps, 'onChange'> & {
  onChange(value: string): void
}

export const LegacyDateInput: TFCC<TLegacyDateInputProps> = ({ value, onChange, ...otherProps }) => (
  <DateInput onChange={(date) => onChange(formatDate(date))} value={value} {...otherProps} />
)
